import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { showApiErrorToast, showSuccessToast } from '@utils'
import { useLanguage } from '@context'
import { Platform } from 'react-native'

interface CreateMemoryData {
  uri: string
  description?: string
  sharedWithAccountIds?: string[]
  albumId?: string
  silent?: boolean
}

/**
 * expo-image-picker on web returns the asset as a `data:` URL produced by
 * `FileReader.readAsDataURL`. `fetch(dataUrl)` is unreliable across
 * browsers / CSP setups (Firefox surfaces a generic `NetworkError` before
 * any HTTP request is even attempted), so we decode the base64 payload
 * directly into a Blob instead.
 */
const dataUrlToBlob = (dataUrl: string): Blob => {
  const commaIdx = dataUrl.indexOf(',')
  if (!dataUrl.startsWith('data:') || commaIdx === -1) {
    throw new Error('Expected a data URL from the image picker')
  }
  const meta = dataUrl.slice(5, commaIdx)
  const payload = dataUrl.slice(commaIdx + 1)
  const isBase64 = meta.endsWith(';base64')
  const mime = (isBase64 ? meta.slice(0, -7) : meta) || 'image/jpeg'

  if (!isBase64) {
    return new Blob([decodeURIComponent(payload)], { type: mime })
  }
  const bytes = atob(payload)
  const buffer = new Uint8Array(bytes.length)
  for (let i = 0; i < bytes.length; i++) buffer[i] = bytes.charCodeAt(i)
  return new Blob([buffer], { type: mime })
}

const createMemory = async (data: CreateMemoryData): Promise<string> => {
  const formData = new FormData()

  if (Platform.OS === 'web') {
    const blob = dataUrlToBlob(data.uri)
    formData.append('file', blob, 'memory.jpg')
  } else {
    // Native: pass the file URI directly
    formData.append('file', {
      uri: data.uri,
      type: 'image/jpeg',
      name: 'memory.jpg',
    } as any)
  }

  if (data.description) {
    formData.append('description', data.description)
  }

  const validIds = (data.sharedWithAccountIds ?? []).filter(
    (id): id is string => typeof id === 'string' && id.trim() !== '',
  )
  validIds.forEach((id) => formData.append('sharedWithAccountIds', id))

  if (data.albumId) {
    formData.append('albumId', data.albumId)
  }

  const { data: responseData } = await api.post<string>('/memories', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  return responseData
}

export const useCreateMemory = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()

  return useMutation<string, Error, CreateMemoryData>({
    mutationFn: createMemory,
    onSuccess: async (_data, { silent }) => {
      if (!silent) showSuccessToast(t('toast.success'), t('toast.memoryCreated'))
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['memories'] }),
        queryClient.invalidateQueries({ queryKey: ['myMemories'] }),
        queryClient.invalidateQueries({ queryKey: ['contact-memories'] }),
        queryClient.invalidateQueries({ queryKey: ['album-memories'] }),
      ])
    },
    onError: (error, { silent }) => {
      if (!silent) showApiErrorToast(t, error)
    },
  })
}
